import {
  type AnalyticsEvent,
  type ConfigApi,
  type DiscoveryApi,
  type FetchApi,
  type AnalyticsApi,
} from '@backstage/core-plugin-api';
import {
  MetricsClient,
  type MetricRequest,
} from '@internal/plugin-neo4j-analytics-common';
import { backOff } from 'exponential-backoff';

import { type AnalyticsEventHandler } from './handlers';
import {
  EXHAUSTED_RETRIES_ERROR_BASE,
  FAILED_TO_SEND_METRIC_ERROR_BASE,
} from './const';
import { BACKOFF_OPTIONS } from './config';

type AnalyticsOptions = {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
};

export class Analytics implements AnalyticsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly metricsClient: MetricsClient;
  private readonly eventHandlers: AnalyticsEventHandler[];

  private constructor(
    _configApi: ConfigApi,
    options: AnalyticsOptions,
    eventHandlers: AnalyticsEventHandler[] = [],
  ) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
    this.eventHandlers = eventHandlers;

    this.metricsClient = new MetricsClient({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
    });
  }

  /**
   * Keep parity with other analytics plugins by using `fromConfig`
   * even though `configApi` is not used, at the moment.
   */
  static fromConfig(
    configApi: ConfigApi,
    options: AnalyticsOptions,
    eventHandlers: AnalyticsEventHandler[] = [],
  ): Analytics {
    return new Analytics(configApi, options, eventHandlers);
  }

  async captureEvent(event: AnalyticsEvent): Promise<void> {
    const metricRequests = this.analyticsEventToMetricRequests(event);

    for (const metricRequest of metricRequests) {
      try {
        await backOff(() => this.sendMetric(metricRequest), BACKOFF_OPTIONS);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `${EXHAUSTED_RETRIES_ERROR_BASE} ${metricRequest.name}: ${error}`,
        );
      }
    }
  }

  // Transforms an analytics event to a metric request
  private analyticsEventToMetricRequests(
    event: AnalyticsEvent,
  ): MetricRequest[] {
    const handler = this.eventHandlers.find(h => h.canHandle(event.action));
    if (!handler) {
      // eslint-disable-next-line no-console
      console.warn(`No handler found for event action: ${event.action}`);
      return [];
    }

    return handler.handle(event);
  }

  private async sendMetric(metric: MetricRequest): Promise<void> {
    const response = await this.metricsClient.captureMetric({
      body: metric,
    });

    if (!response.ok) {
      const message = await response.text().catch(() => response.statusText);
      throw new Error(
        `${FAILED_TO_SEND_METRIC_ERROR_BASE} ${metric.name}: ${message}`,
      );
    }
  }
}
